"use client";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { motion } from "framer-motion";
import { Cloud, Code, Database, Globe, Palette, Zap } from "lucide-react";
import { IconType } from "react-icons";
import { BiServer } from "react-icons/bi";
import { SiGraphql } from "react-icons/si";
// import { Progress } from './ui/progress';

export const skillCategories: {
  icon: IconType;
  title: string;
  description: string;
  skills: { name: string; level: number; experience: string }[];
}[] = [
  {
    icon: Code,
    title: "Frontend Development",
    description: "client-side languages and frameworks",
    skills: [
      // { name: "JavaScript", level: 92, experience: "6+ years" },
      { name: "JS, TypeScript", level: 95, experience: "6+ years" },
      { name: "React.js, Vite.js, Webpack", level: 80, experience: "3+ years" },
      // { name: "React.js", level: 92, experience: "5+ years" },
      { name: "Next.js", level: 92, experience: "3+ years" },
      { name: "Angular", level: 25, experience: "3+ years" },
      { name: "Html5, Css3", level: 90, experience: "4+ years" },
      { name: "MUI, TailwindCSS, Shadcn", level: 85, experience: "3+ years" },
      // { name: "MUI", level: 85, experience: "3+ years" },
      // { name: "Shadcn", level: 85, experience: "2+ years" },
      { name: "Framer Motion/Gsap", level: 70, experience: "1+ years" },
      {
        // name: "AWS EC2, Azure Devops, Vercel, Netlify, Railway",
        name: "AWS EC2, Azure Devops",
        level: 90,
        experience: "2+ years",
      },
    ],
  },
  {
    icon: BiServer,
    title: "Backend Development",
    description: "Server-side languages and Fremworks",
    skills: [
      {
        name: "Node.js (Nest.js, Koa.js, Express.js)",
        level: 92,
        experience: "3+ years",
      },
      { name: "Python", level: 80, experience: "2+ years" },
      { name: "FastAPI, Django", level: 70, experience: "1+ year" },
      { name: "C#, Asp.Net Core", level: 75, experience: "2+ years" },
      { name: "Java, Spring Boot", level: 75, experience: "2+ years" },
      // { name: "C++, Qt", level: 70, experience: "2+ years" },
    ],
  },
  {
    icon: Database,
    title: "Databases",
    description: "Server databases",
    skills: [
      { name: "MongoDB", level: 85, experience: "3.5+ years" },
      { name: "PostgreSQL, Prisma", level: 80, experience: "2+ year" },
      {
        name: "Redis, RedisLock, BullMq",
        level: 75,
        experience: "2+ years",
      },
      {
        name: "Elasticsearch, Aloglia",
        level: 75,
        experience: "2+ years",
      },
    ],
  },
  {
    icon: Cloud,
    title: "DevOps & Cloud",
    description: "Cloud platforms and devops technologies",
    skills: [
      {
        name: "AWS (IAM, S3, SNS/SQS, MediaConvert, Transcoder, DynamoDB, EC2, Vision, Rekognition",
        level: 85,
        experience: "3+ years",
      },
      {
        name: "GCP (Translate, Maps, Vision AI, BigQuery, Gemini/Vertex AI, Firestore, Cloud Functions, Cloud Storage, Cloud CDN)",
        level: 75,
        experience: "2+ year",
      },
      {
        name: "Microsoft Azure (Devops, AI Search, AI Video, Computer Vision)",
        level: 70,
        experience: "2+ year",
      },
      { name: "Docker", level: 75, experience: "2+ years" },
      { name: "Nginx", level: 70, experience: "1+ year" },
      {
        name: "CI/CD",
        level: 70,
        experience: "2+ year",
      },
    ],
  },
  {
    icon: SiGraphql,
    title: "API Tools",
    description: "Server-side Apis",
    skills: [
      { name: "GraphQL, Apollo", level: 88, experience: "3+ years" },
      { name: "Rest Api", level: 85, experience: "4+ years" },
      { name: "tRPC", level: 50, experience: "0.5+ year" },
    ],
  },
  {
    icon: Zap,
    title: "AI & Machine Learning",
    description: "AI integration and Machine Learning technologies",
    skills: [
      { name: "Groq AI", level: 85, experience: "1+ year" },
      {
        name: "LLM APIs, OpenAI, Gemini",
        level: 80,
        experience: "2+ year",
      },
      { name: "LangChain", level: 75, experience: "1+ year" },
      { name: "Python", level: 80, experience: "3+ years" },
      { name: "TensorFlow", level: 70, experience: "1+ year" },
    ],
  },
  {
    icon: Palette,
    title: "Design & Ui/Ux",
    description: "Design tools and user experience",
    skills: [
      { name: "Framer Motion", level: 85, experience: "2+ years" },
      { name: "Shadcn", level: 90, experience: "1+ year" },
      { name: "Material-UI", level: 85, experience: "2+ years" },
      { name: "Figma", level: 50, experience: "1+ year" },
      {
        name: "Ui/Ux",
        level: 80,
        experience: "2+ years",
      },
      { name: "User Experience", level: 90, experience: "3+ years" },
    ],
  },
  {
    icon: Globe,
    title: "Other Tools",
    description: "Development tools and methodologies",
    skills: [
      { name: "Git, GitHub", level: 95, experience: "4+ years" },
      {
        name: "Vs Code, Webstorm",
        level: 95,
        experience: "4+ years",
      },
      { name: "Postman", level: 85, experience: "3+ years" },
      {
        name: "Jest, React testing library",
        level: 80,
        experience: "2+ years",
      },
      { name: "Agile, Scrum", level: 85, experience: "3+ years" },
      { name: "Data engineering", level: 50, experience: "3+ years" },
      { name: "Unit Tests (Jest)", level: 50, experience: "3+ years" },
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

export function SkillsComponent() {
  return (
    <section
      id="skills"
      className="p-4"
      // className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-purple-50 dark:from-black dark:via-gray-900 dark:to-purple-900/20"
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="grid lg:grid-cols-3 md:grid-cols-2 gap-8 mb-16"
      >
        {skillCategories.map((category) => (
          <motion.div key={category.title} variants={itemVariants}>
            <Card className="h-full bg-white/60 dark:bg-gray-800/60 border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-2xl">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <category.icon className="h-10 w-10 text-purple-600 dark:text-purple-400" />
                  <div>
                    <CardTitle className="text-2xl font-bold">
                      {category.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-400">
                      {category.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {category.skills.map((skill) => (
                  <div key={skill.name} className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800 dark:text-white">
                        {skill.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                        >
                          {skill.experience}
                        </Badge>
                        <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                          {skill.level}%
                        </span>
                      </div>
                    </div>
                    {/* <Progress value={skill.level} className="h-2" /> */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <motion.div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        viewport={{ once: true }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
